import type { Granularity } from '@/types/enums';

export interface AnalyticsSeriesPoint {
  t: string;
  v: number | null;
}

export interface AnalyticsChartPoint extends AnalyticsSeriesPoint {
  x: number;
  partial: boolean;
}

export type AnalyticsSeriesMode = 'bucket' | 'cumulative';

function nextBucket(timestamp: number, granularity: Granularity): number {
  const date = new Date(timestamp);
  if (granularity === 'MONTH') date.setUTCMonth(date.getUTCMonth() + 1);
  else if (granularity === 'WEEK') date.setUTCDate(date.getUTCDate() + 7);
  else if (granularity === 'DAY') date.setUTCDate(date.getUTCDate() + 1);
  else date.setUTCHours(date.getUTCHours() + 1);
  return date.getTime();
}

export function prepareAnalyticsSeries(
  points: AnalyticsSeriesPoint[],
  granularity: Granularity,
  mode: AnalyticsSeriesMode = 'bucket',
  now = Date.now(),
): AnalyticsChartPoint[] {
  const sorted = points
    .map((point) => ({ ...point, x: Date.parse(point.t) }))
    .filter((point) => Number.isFinite(point.x))
    .sort((a, b) => a.x - b.x);
  const result: AnalyticsChartPoint[] = [];
  let cumulative = 0;
  for (const point of sorted) {
    const previous = result.at(-1);
    if (previous) {
      const expected = nextBucket(previous.x, granularity);
      if (point.x > expected) {
        // One explicit null breaks the line; the numeric time axis preserves
        // the full gap without allocating every absent bucket for All time.
        result.push({ t: new Date(expected).toISOString(), x: expected, v: null, partial: false });
      }
    }
    const value = point.v !== null && Number.isFinite(point.v) ? point.v : null;
    if (value !== null) cumulative += value;
    result.push({
      ...point,
      v: value === null ? null : mode === 'cumulative' ? cumulative : value,
      partial: point.x <= now && nextBucket(point.x, granularity) > now,
    });
  }
  return result;
}

export function formatAnalyticsBucket(
  value: string | number,
  granularity: Granularity,
  locale: string,
  detailed = false,
): string {
  const timestamp = typeof value === 'number' ? value : Date.parse(value);
  if (!Number.isFinite(timestamp)) return '—';
  const date = new Date(timestamp);
  const dateFormat = new Intl.DateTimeFormat(locale, {
    timeZone: 'UTC',
    day: 'numeric',
    month: detailed ? 'long' : 'short',
    ...(detailed ? { year: 'numeric' as const } : {}),
  });
  const suffix = detailed ? ' UTC' : '';
  if (granularity === 'HOUR') {
    const timeFormat = new Intl.DateTimeFormat(locale, {
      timeZone: 'UTC',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    });
    const interval = detailed
      ? `${timeFormat.format(date)}–${timeFormat.format(nextBucket(timestamp, granularity))}`
      : timeFormat.format(date);
    return `${dateFormat.format(date)} · ${interval}${suffix}`;
  }
  if (granularity === 'WEEK') {
    const end = nextBucket(timestamp, granularity) - 1;
    return `${dateFormat.format(date)} – ${dateFormat.format(end)}${suffix}`;
  }
  if (granularity === 'MONTH') {
    return `${new Intl.DateTimeFormat(locale, {
      timeZone: 'UTC',
      month: detailed ? 'long' : 'short',
      year: 'numeric',
    }).format(date)}${suffix}`;
  }
  return `${dateFormat.format(date)}${suffix}`;
}

export function formatAnalyticsValue(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value);
}
